import { useId, type ReactNode } from 'react';

/**
 * The redesign's card with a heading row (DS `.card` + `.card-head`): the
 * title on the left, a link or a short note on the right, then the body and an
 * optional footer line (`.card-foot`, what a colour or a "—" means).
 * `flush` drops the body padding for lists and tables that run edge to edge.
 */
export interface CardProps {
  title: string;
  aside?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  /** An anchor other links jump to ("Définir un objectif ↓"). */
  id?: string;
  className?: string;
  children: ReactNode;
}

export const CARD_FRAME = 'rounded-ds-lg border border-border bg-surface shadow-ds-xs';

export function Card({ title, aside, footer, flush = false, id, className = '', children }: CardProps) {
  const headingId = useId();
  return (
    <section id={id} aria-labelledby={headingId} className={`${CARD_FRAME} min-w-0 overflow-hidden ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-5 py-3.5">
        <h2 id={headingId} className="text-[15px] font-semibold leading-[22px] text-ink">
          {title}
        </h2>
        {aside}
      </div>
      {flush ? children : <div className="px-5 py-4">{children}</div>}
      {footer ? <div className="border-t border-border px-5 py-3 text-ds-xs text-ink-3">{footer}</div> : null}
    </section>
  );
}

/** One row of a card's list (DS `.list-row`): a link or a plain row, divided from the next. */
export const LIST_ROW = 'flex min-w-0 items-center gap-3 border-b border-border px-5 py-3 last:border-0';
